import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus, X } from 'lucide-react';
import '../styles/MultiVal.css';

const createRowKey = () => `mv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const toSlug = (value = '') => String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const parseJsonSafely = (value, fallback) => {
    if (!value) return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

const normalizeColumn = (column, index) => {
    if (typeof column === 'string') {
        const key = toSlug(column) || `column_${index + 1}`;
        return { key, label: column, type: 'text', options: [], isTotal: false };
    }

    if (Array.isArray(column)) {
        const key = toSlug(column[0] || column[1] || `column_${index + 1}`) || `column_${index + 1}`;
        return {
            key,
            label: column[1] || column[0] || `Column ${index + 1}`,
            type: 'text',
            options: [],
            isTotal: false
        };
    }

    if (column && typeof column === 'object') {
        const key = toSlug(
            column.key
            || column.col_key
            || column.name
            || column.col_name
            || column.label
            || column.title
            || `column_${index + 1}`
        ) || `column_${index + 1}`;

        const rawType = column.type
            || column.col_type
            || column.fieldType
            || column.field_id
            || 'text';

        const rawOptions = column.options
            || column.col_options
            || [];

        const isTotal = [1, '1', true, 'true', 'yes'].includes(column.isTotal)
            || [1, '1', true, 'true', 'yes'].includes(column.col_total)
            || [1, '1', true, 'true', 'yes'].includes(column.total);

        return {
            key,
            label: column.label || column.col_name || column.name || column.title || column.key || `Column ${index + 1}`,
            type: rawType,
            options: rawOptions,
            isTotal
        };
    }

    return {
        key: `column_${index + 1}`,
        label: `Column ${index + 1}`,
        type: 'text',
        options: [],
        isTotal: false
    };
};

const normalizeColumns = (options) => {
    const parsed = parseJsonSafely(options, options);
    const rawColumns = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.columns)
            ? parsed.columns
            : [];

    const columns = rawColumns.map(normalizeColumn).filter(Boolean);
    if (columns.length > 0) return columns;

    return [{ key: 'value', label: 'Value', type: 'text', options: [] }];
};

const normalizeRows = (value, columns) => {
    const parsed = parseJsonSafely(value, value);
    const rows = Array.isArray(parsed) ? parsed : [];

    return rows.map((row) => {
        const nextRow = { __rowKey: createRowKey() };
        columns.forEach((column) => {
            nextRow[column.key] = row?.[column.key] ?? '';
        });
        return nextRow;
    });
};

const syncRowsWithExternal = (incomingRows, existingRows, columns) => {
    return incomingRows.map((incomingRow, index) => {
        const existingRow = existingRows[index];
        const nextRow = {
            __rowKey: existingRow?.__rowKey || incomingRow.__rowKey || createRowKey()
        };

        columns.forEach((column) => {
            nextRow[column.key] = incomingRow?.[column.key] ?? '';
        });

        return nextRow;
    });
};

const createEmptyRow = (columns) => {
    const row = { __rowKey: createRowKey() };
    columns.forEach((column) => {
        row[column.key] = '';
    });
    return row;
};

const isRowEmpty = (row, columns) => columns.every((column) => {
    const cellValue = row?.[column.key];
    if (Array.isArray(cellValue)) return cellValue.length === 0;
    return cellValue === '' || cellValue === null || cellValue === undefined;
});

const normalizeType = (type) => {
    switch (String(type)) {
        case '1':
        case 'text':
            return 'text';
        case '2':
        case 'textarea':
            return 'textarea';
        case '3':
        case 'money':
        case 'currency':
            return 'money';
        case '4':
        case 'percent':
            return 'percent';
        case '5':
        case 'number':
            return 'number';
        case '6':
        case 'date':
            return 'date';
        case '7':
        case 'time':
            return 'time';
        case '10':
        case 'list':
        case 'select':
            return 'list';
        case '12':
        case 'boolean':
            return 'boolean';
        default:
            return 'text';
    }
};

const normalizeSelectOptions = (options) => {
    const parsed = parseJsonSafely(options, options);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((option, index) => {
        if (Array.isArray(option)) {
            return { value: option[0], label: option[1] ?? option[0] };
        }
        if (option && typeof option === 'object') {
            const optionValue = option.value ?? option.id ?? option.key ?? index;
            return { value: optionValue, label: option.label ?? option.name ?? optionValue };
        }
        return { value: index, label: option };
    });
};

const parseNumericValue = (rawValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') return null;

    const numericValue = Number(String(rawValue).replace(/[$,%\s,]/g, ''));
    return Number.isFinite(numericValue) ? numericValue : null;
};

const formatTotalValue = (total, columnType) => {
    if (!Number.isFinite(total)) return '0';

    const normalizedType = normalizeType(columnType);

    if (normalizedType === 'money') {
        return total.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    if (normalizedType === 'percent') {
        return `${total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}%`;
    }

    return total.toLocaleString(undefined, {
        minimumFractionDigits: Math.abs(total % 1) > 0.000001 ? 2 : 0,
        maximumFractionDigits: 2
    });
};

const renderCellInput = ({ column, row, rowIndex, updateCell }) => {
    const value = row?.[column.key] ?? '';
    const type = normalizeType(column.type);

    if (type === 'textarea') {
        return (
            <textarea
                value={value}
                placeholder={`${column.label}...`}
                onChange={(event) => updateCell(rowIndex, column.key, event.target.value)}
                rows={2}
            />
        );
    }

    if (type === 'list') {
        const options = normalizeSelectOptions(column.options);
        return (
            <div className='filter-group' style={{ width: '100%' }}>
                <div className='select-wrapper' style={{ width: '100%' }}>
                    <select
                        className='filter-select'
                        value={value}
                        onChange={(event) => updateCell(rowIndex, column.key, event.target.value)}
                    >
                        <option value="">Select...</option>
                        {options.map((option) => (
                            <option key={`${column.key}_${option.value}`} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <ChevronDown size={16} className='select-chevron' />
                </div>
            </div>
        );
    }

    if (type === 'boolean') {
        return (
            <select
                value={value === '' || value === null || value === undefined ? '2' : String(value)}
                onChange={(event) => updateCell(rowIndex, column.key, event.target.value)}
            >
                <option value="2">Unknown</option>
                <option value="0">Yes</option>
                <option value="1">No</option>
            </select>
        );
    }

    return (
        <input
            type={type === 'date' || type === 'time' ? type : (type === 'number' || type === 'money' || type === 'percent' ? 'number' : 'text')}
            step={type === 'number' || type === 'money' || type === 'percent' ? '0.01' : undefined}
            inputMode={type === 'number' || type === 'money' || type === 'percent' ? 'decimal' : undefined}
            placeholder={type === 'number' || type === 'money' || type === 'percent' ? '0.00' : `${column.label}...`}
            value={value}
            onChange={(event) => updateCell(rowIndex, column.key, event.target.value)}
        />
    );
};

export const MultiVal = ({ value = [], values, onChange = () => {}, options = [], fieldName = 'Multi Value' }) => {
    const columns = useMemo(() => normalizeColumns(options), [options]);
    const actionColumnWidth = 44;
    const minColumnWidth = 120;
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(null);
    const externalValue = value ?? values;
    const externalRows = useMemo(
        () => normalizeRows(externalValue ?? [], columns),
        [externalValue, columns]
    );
    const [rows, setRows] = useState(() => externalRows);

    useEffect(() => {
        setRows((prevRows) => syncRowsWithExternal(externalRows, prevRows, columns));
    }, [externalRows]);

    useEffect(() => {
        const element = containerRef.current;
        if (!element || typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[entries.length - 1];
            if (entry) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const isCompact = containerWidth !== null
        && containerWidth < (columns.length * minColumnWidth) + actionColumnWidth;

    const displayRows = useMemo(
        () => rows.filter((row) => !isRowEmpty(row, columns)),
        [rows, columns]
    );

    const totalColumn = useMemo(
        () => columns.find((column) => column.isTotal) || columns[columns.length - 1],
        [columns]
    );

    const totalValue = useMemo(() => {
        if (!totalColumn) return 0;

        return displayRows.reduce((sum, row) => {
            const numericValue = parseNumericValue(row?.[totalColumn.key]);
            return numericValue !== null ? sum + numericValue : sum;
        }, 0);
    }, [displayRows, totalColumn]);

    const formattedTotal = useMemo(() => {
        return formatTotalValue(totalValue, totalColumn?.type);
    }, [totalValue, totalColumn]);

    const emitRows = (nextRows) => {
        const sanitizedRows = nextRows
            .map((row) => {
                const nextRow = {};
                columns.forEach((column) => {
                    nextRow[column.key] = row?.[column.key] ?? '';
                });
                return nextRow;
            })
            .filter((row) => !isRowEmpty(row, columns));

        onChange(sanitizedRows);
    };

    const updateCell = (rowIndex, columnKey, cellValue) => {
        setRows((prevRows) => {
            const nextRows = prevRows.map((row, index) => index === rowIndex
                ? { ...row, [columnKey]: cellValue }
                : row);
            emitRows(nextRows);
            return nextRows;
        });
    };

    const addRow = () => {
        setRows((prevRows) => {
            return [...prevRows, createEmptyRow(columns)];
        });
    };

    const removeRow = (rowIndex) => {
        setRows((prevRows) => {
            const nextRows = prevRows.filter((_, index) => index !== rowIndex);
            emitRows(nextRows);
            return nextRows;
        });
    };

    return (
        <div className="multi-val-field form-group nm" ref={containerRef}>
            <div className="multi-val-header">
                <span className="subtext">
                    {fieldName} - {formattedTotal}
                </span>
                <Plus size={16} style={{ color: 'var(--subtext-color)', cursor: 'pointer' }} onClick={addRow} />
            </div>
            {isCompact ? (
                <div className="multi-val-cards">
                    {rows.map((row, rowIndex) => (
                        <div key={row.__rowKey} className="multi-val-card">
                            <div className="multi-val-card-header">
                                <span className="multi-val-card-title subtext">{fieldName} {rowIndex + 1}</span>
                                <button
                                    type="button"
                                    className="multi-val-remove-button"
                                    onClick={() => removeRow(rowIndex)}
                                    aria-label={`Remove ${fieldName} row ${rowIndex + 1}`}
                                    title="Remove row"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                            <div className="multi-val-card-grid">
                                {columns.map((column) => (
                                    <div key={`${row.__rowKey}_${column.key}`} className="multi-val-card-cell">
                                        <span className="multi-val-card-label">{column.label}</span>
                                        {renderCellInput({ column, row, rowIndex, updateCell })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ overflowX: 'auto', width: '100%' }}>
                    <table className='multi-val-table'>
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="subtext"
                                        style={{ width: `calc((100% - ${actionColumnWidth}px) / ${columns.length})` }}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                                <th className="multi-val-action-header" style={{ width: `${actionColumnWidth}px`, textAlign: 'center' }}>
                                    -
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={row.__rowKey}>
                                    {columns.map((column) => (
                                        <td key={`${row.__rowKey}_${column.key}`} style={{ width: `calc((100% - ${actionColumnWidth}px) / ${columns.length})` }}>
                                            {renderCellInput({ column, row, rowIndex, updateCell })}
                                        </td>
                                    ))}
                                    <td className="multi-val-action-cell" style={{ width: `${actionColumnWidth}px` }}>
                                        <button
                                            type="button"
                                            className="multi-val-remove-button"
                                            onClick={() => removeRow(rowIndex)}
                                            aria-label={`Remove ${fieldName} row ${rowIndex + 1}`}
                                            title="Remove row"
                                        >
                                            <X size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};